import { useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, X, BookOpen, Activity, Wind, Target, GraduationCap, CheckCircle, XCircle, RotateCcw, AlertTriangle, Lightbulb, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import KennelSEO, { localBusinessSchema, breadcrumbSchema } from "../kennel/KennelSEO";
import { LAYERS_META, GROUP_COLORS } from "./data/layers";

const BASE = "https://anatomia.wolfevomkatzenberg.pl";

const INFO_TABS = [
  { id: "description", label: "Opis", icon: BookOpen },
  { id: "function", label: "Funkcja", icon: Activity },
  { id: "movement", label: "Ruch", icon: Wind },
  { id: "breed", label: "Rasa", icon: Target },
];

/* Helper: hex color to rgba */
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

/* ── QUIZ ── */
const Quiz = ({ questions }) => {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const q = questions[current];
  const score = answered.filter(a => a.correct).length;
  const isAnswered = selected !== null;

  const handleSelect = (idx) => {
    if (isAnswered) return;
    setSelected(idx);
    setAnswered(prev => [...prev, { qIdx: current, selected: idx, correct: idx === q.correct }]);
  };

  const next = () => {
    if (current < questions.length - 1) {
      setCurrent(current + 1);
      setSelected(null);
    } else {
      setShowResult(true);
    }
  };

  const restart = () => {
    setCurrent(0);
    setSelected(null);
    setAnswered([]);
    setShowResult(false);
  };

  if (showResult) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="quiz-result">
        <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#D4AF37] flex items-center justify-center">
          <span className="text-[#D4AF37] font-serif text-2xl">{pct}%</span>
        </div>
        <h3 className="font-serif text-xl text-[#F5F5F5] mb-2">Wynik: {score}/{questions.length}</h3>
        <p className="text-[#B8B8B8]/50 text-sm mb-8">
          {pct >= 80 ? "Doskonale! Znasz budowę owczarka." : pct >= 50 ? "Dobrze! Warto jeszcze powtórzyć." : "Spróbuj jeszcze raz!"}
        </p>
        <button onClick={restart} className="px-6 py-2.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all flex items-center gap-2 mx-auto" data-testid="quiz-restart">
          <RotateCcw className="w-3.5 h-3.5" /> SPRÓBUJ PONOWNIE
        </button>
      </motion.div>
    );
  }

  return (
    <div data-testid="quiz-section">
      <div className="flex items-center justify-between mb-6">
        <span className="text-[10px] tracking-[0.15em] text-[#B8B8B8]/40">PYTANIE {current + 1} / {questions.length}</span>
        <div className="flex gap-1.5">
          {questions.map((_, i) => (
            <div key={i} className={`w-8 h-1 transition-colors ${i < answered.length ? (answered[i]?.correct ? "bg-green-500" : "bg-red-500/60") : i === current ? "bg-[#D4AF37]" : "bg-[#2A2A2D]"}`} />
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
          <h4 className="text-[#F5F5F5] text-sm md:text-base mb-6 leading-relaxed">{q.q}</h4>
          <div className="space-y-2.5">
            {q.options.map((opt, idx) => {
              const isCorrect = idx === q.correct;
              const isSelected = selected === idx;
              return (
                <button key={idx} onClick={() => handleSelect(idx)} disabled={isAnswered}
                  className={`w-full text-left p-4 border text-sm transition-all duration-300 flex items-center gap-3 ${
                    isAnswered
                      ? isCorrect ? "border-green-500/50 bg-green-500/10 text-green-400"
                        : isSelected ? "border-red-500/50 bg-red-500/10 text-red-400"
                        : "border-[#2A2A2D] text-[#B8B8B8]/30"
                      : "border-[#2A2A2D] text-[#B8B8B8] hover:border-[#D4AF37]/40 hover:text-[#F5F5F5] cursor-pointer"
                  }`} data-testid={`quiz-option-${idx}`}
                >
                  <span className={`w-6 h-6 border flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    isAnswered && isCorrect ? "border-green-500 text-green-400" : isAnswered && isSelected ? "border-red-500 text-red-400" : "border-[#D4AF37]/30 text-[#D4AF37]/50"
                  }`}>
                    {isAnswered && isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : isAnswered && isSelected ? <XCircle className="w-3.5 h-3.5" /> : String.fromCharCode(65 + idx)}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          {isAnswered && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20">
              <p className="text-xs text-[#B8B8B8]/70 leading-relaxed">{q.explanation}</p>
            </motion.div>
          )}
          {isAnswered && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-6 flex justify-end">
              <button onClick={next} className="px-6 py-2.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all flex items-center gap-2" data-testid="quiz-next">
                {current < questions.length - 1 ? "DALEJ" : "POKAŻ WYNIK"} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/* ── FUN FACTS SLIDER ── */
const FunFactsSlider = ({ facts }) => {
  const [current, setCurrent] = useState(0);
  const prev = () => setCurrent(i => (i - 1 + facts.length) % facts.length);
  const next = () => setCurrent(i => (i + 1) % facts.length);

  return (
    <section className="border-b border-[#2A2A2D] px-4 md:px-8 py-12 md:py-16">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-[10px] tracking-[0.3em] text-[#D4AF37]">CIEKAWOSTKI</span>
        </div>
        <div className="relative bg-[#141416] border border-[#2A2A2D] p-8 md:p-10" data-testid="fun-facts">
          <AnimatePresence mode="wait">
            <motion.p key={current}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              className="text-[#F5F5F5] text-sm md:text-base leading-relaxed min-h-[48px]"
            >
              {facts[current]}
            </motion.p>
          </AnimatePresence>
          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-1.5">
              {facts.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`w-8 h-1 transition-colors ${i === current ? "bg-[#D4AF37]" : "bg-[#2A2A2D] hover:bg-[#D4AF37]/30"}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prev} className="w-8 h-8 border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8]/40 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-all" data-testid="fact-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={next} className="w-8 h-8 border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8]/40 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-all" data-testid="fact-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── MAIN ATLAS LAYER ── */
const AtlasLayer = ({ layer, quiz, seoTitle, seoDesc }) => {
  const [selectedPart, setSelectedPart] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [infoTab, setInfoTab] = useState("description");
  const [learningMode, setLearningMode] = useState(false);
  const [learningStep, setLearningStep] = useState(0);
  const navigate = useNavigate();
  const modelRef = useRef(null);

  const learningPart = learningMode ? layer.points[learningStep] : null;
  const activePart = learningMode ? learningPart?.id : selectedPart;
  const activeData = learningMode ? learningPart : layer.points.find(p => p.id === selectedPart);

  /* Group points by region for legend */
  const groupedPoints = useMemo(() => {
    const groups = {};
    layer.points.forEach(pt => {
      const g = pt.group;
      if (!groups[g]) groups[g] = { color: GROUP_COLORS[g]?.fill || "#D4AF37", label: GROUP_COLORS[g]?.label || g, points: [] };
      groups[g].points.push(pt);
    });
    return Object.values(groups);
  }, [layer.points]);

  const handlePartClick = (partId) => {
    if (learningMode) return;
    setSelectedPart(partId === selectedPart ? null : partId);
    setInfoTab("description");
  };

  const startLearning = () => {
    setLearningMode(true);
    setLearningStep(0);
    setSelectedPart(null);
    modelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getColor = (ptId) => {
    const pt = layer.points.find(p => p.id === ptId);
    return GROUP_COLORS[pt?.group]?.fill || "#D4AF37";
  };

  const getAreaFill = (ptId) => {
    const c = getColor(ptId);
    if (activePart === ptId) return hexToRgba(c, 0.20);
    if (hoveredPart === ptId) return hexToRgba(c, 0.10);
    return "transparent";
  };

  const getAreaStroke = (ptId) => {
    const c = getColor(ptId);
    if (activePart === ptId) return hexToRgba(c, 0.75);
    if (hoveredPart === ptId) return hexToRgba(c, 0.40);
    return "transparent";
  };

  const getLabelBg = (ptId) => {
    return getColor(ptId);
  };

  return (
    <div>
      <KennelSEO
        title={seoTitle}
        description={seoDesc}
        canonical={`${BASE}/${layer.slug}`}
        ogImage={`${BASE}${layer.image}`}
        schema={[localBusinessSchema, breadcrumbSchema([
          { name: "Atlas Anatomii", href: BASE },
          { name: layer.name, href: `${BASE}/${layer.slug}` }
        ])]}
      />

      {/* ── HEADER ── */}
      <section className="border-b border-[#2A2A2D] px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          <nav className="flex items-center gap-2 text-[10px] tracking-wider text-[#B8B8B8]/40 mb-6" data-testid="layer-breadcrumbs">
            <Link to="/anatomia/owczarek-niemiecki" className="hover:text-[#D4AF37] transition-colors">ATLAS</Link>
            <span>/</span>
            <span className="text-[#D4AF37]">{layer.name.toUpperCase()}</span>
          </nav>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">WARSTWA</span>
              <h1 className="font-serif text-[clamp(1.6rem,4vw,2.8rem)] text-[#F5F5F5]" data-testid="layer-h1">{layer.name}</h1>
            </div>
            <button onClick={startLearning}
              className="px-5 py-2.5 bg-[#D4AF37] text-[#0B0B0D] text-[10px] font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all flex items-center gap-2 self-start md:self-auto"
              data-testid="start-learning"
            >
              <GraduationCap className="w-3.5 h-3.5" /> TRYB NAUKI
            </button>
          </div>

          {/* Layer switcher */}
          <div className="flex gap-1.5 mt-6 overflow-x-auto no-scrollbar" data-testid="layer-switcher">
            {LAYERS_META.map(l => (
              <button key={l.id}
                onClick={() => l.ready && navigate(`/anatomia/owczarek-niemiecki/${l.slug}`)}
                className={`px-4 py-2 text-[10px] tracking-[0.1em] whitespace-nowrap transition-all shrink-0 ${
                  l.slug === layer.slug
                    ? "bg-[#D4AF37] text-[#0B0B0D] font-bold"
                    : l.ready
                      ? "border border-[#2A2A2D] text-[#B8B8B8] hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                      : "border border-[#2A2A2D]/50 text-[#B8B8B8]/25 cursor-default"
                }`}
                data-testid={`switch-${l.slug}`}
              >
                {l.name.toUpperCase()} {!l.ready && "(wkrótce)"}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE MODEL ── */}
      <section ref={modelRef} className="px-4 md:px-8 py-8 md:py-14">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 lg:gap-10 items-start">

          {/* Left: Image + SVG areas + Legend */}
          <div className="flex-1 w-full min-w-0">
            {/* Learning mode bar */}
            {learningMode && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-4 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/30 p-4 flex items-center justify-between" data-testid="learning-bar"
              >
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs text-[#D4AF37]">TRYB NAUKI — Krok {learningStep + 1} / {layer.points.length}</span>
                </div>
                <button onClick={() => setLearningMode(false)} className="text-[#B8B8B8]/40 hover:text-[#F5F5F5]" data-testid="close-learning">
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {/* Image with SVG overlay */}
            <div className="relative w-full select-none">
              <img src={layer.image} alt={layer.name} className="w-full h-auto block" draggable={false} data-testid="layer-image" />

              {/* SVG area highlights */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ zIndex: 10 }}
                data-testid="svg-overlay"
              >
                <defs>
                  <filter id="glow-gold" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" />
                    <feFlood floodColor="#D4AF37" floodOpacity="0.5" />
                    <feComposite operator="in" in2="SourceAlpha" />
                    <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="glow-green" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" />
                    <feFlood floodColor="#5BA88C" floodOpacity="0.5" />
                    <feComposite operator="in" in2="SourceAlpha" />
                    <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="glow-orange" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" />
                    <feFlood floodColor="#C87F4A" floodOpacity="0.5" />
                    <feComposite operator="in" in2="SourceAlpha" />
                    <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="glow-hover" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
                    <feFlood floodColor="#D4AF37" floodOpacity="0.25" />
                    <feComposite operator="in" in2="SourceAlpha" />
                    <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>

                {layer.points.map((pt) => {
                  const isActive = activePart === pt.id;
                  const isHover = hoveredPart === pt.id && !isActive;
                  const glowFilter = isActive
                    ? (pt.group === "front" ? "url(#glow-green)" : pt.group === "hind" ? "url(#glow-orange)" : "url(#glow-gold)")
                    : isHover ? "url(#glow-hover)" : "none";

                  return (
                    <polygon
                      key={pt.id}
                      points={pt.area}
                      fill={getAreaFill(pt.id)}
                      stroke={getAreaStroke(pt.id)}
                      strokeWidth={isActive ? "0.4" : "0.25"}
                      strokeLinejoin="round"
                      filter={glowFilter}
                      className="cursor-pointer"
                      style={{ transition: "fill 0.25s ease, stroke 0.25s ease" }}
                      onClick={() => handlePartClick(pt.id)}
                      onMouseEnter={() => !learningMode && setHoveredPart(pt.id)}
                      onMouseLeave={() => setHoveredPart(null)}
                      data-testid={`area-${pt.id}`}
                    />
                  );
                })}
              </svg>

              {/* Floating labels */}
              {layer.points.map((pt) => {
                const show = activePart === pt.id || hoveredPart === pt.id;
                if (!show) return null;
                const bgColor = getLabelBg(pt.id);
                return (
                  <div key={`lbl-${pt.id}`} className="absolute pointer-events-none z-20"
                    style={{ left: `${pt.position.x}%`, top: `${pt.position.y}%`, transform: "translate(-50%, -120%)" }}
                  >
                    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}
                      className="whitespace-nowrap text-[8px] md:text-[9px] tracking-[0.12em] font-bold px-2.5 py-1"
                      style={{ backgroundColor: bgColor, color: "#0B0B0D" }}
                    >
                      {pt.name.toUpperCase()}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-l-transparent border-r-transparent"
                        style={{ borderTopColor: bgColor }}
                      />
                    </motion.div>
                  </div>
                );
              })}

              {/* Bottom gradient */}
              <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0B0B0D] to-transparent pointer-events-none z-[5]" />
            </div>

            {/* Grouped legend */}
            <div className="mt-5 space-y-3" data-testid="layer-legend">
              {groupedPoints.map((grp, gi) => (
                <div key={gi}>
                  {/* Group label */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: grp.color }} />
                    <span className="text-[9px] tracking-[0.15em] font-medium" style={{ color: grp.color }}>{grp.label.toUpperCase()}</span>
                  </div>
                  {/* Items */}
                  <div className="flex flex-wrap gap-1 pl-4">
                    {grp.points.map(pt => (
                      <button key={pt.id} onClick={() => handlePartClick(pt.id)}
                        className="text-[9px] tracking-wider px-2.5 py-1 border transition-all duration-200"
                        style={
                          activePart === pt.id
                            ? { backgroundColor: grp.color, color: "#0B0B0D", borderColor: grp.color, fontWeight: 700 }
                            : { backgroundColor: "transparent", color: `${grp.color}80`, borderColor: "#2A2A2D" }
                        }
                        onMouseOver={e => { if (activePart !== pt.id) { e.currentTarget.style.borderColor = `${grp.color}60`; e.currentTarget.style.color = grp.color; }}}
                        onMouseOut={e => { if (activePart !== pt.id) { e.currentTarget.style.borderColor = "#2A2A2D"; e.currentTarget.style.color = `${grp.color}80`; }}}
                        data-testid={`legend-${pt.id}`}
                      >
                        {pt.name.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Info Panel */}
          <div className="lg:w-[400px] w-full lg:sticky lg:top-[72px]">
            <AnimatePresence mode="wait">
              {activeData ? (
                <motion.div key={activeData.id}
                  initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="bg-[#141416] border border-[#2A2A2D] overflow-hidden" data-testid="info-panel"
                >
                  {/* Accent bar in group color */}
                  <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.4, delay: 0.1 }}
                    className="h-[2px] origin-left"
                    style={{ background: `linear-gradient(90deg, transparent, ${getColor(activeData.id)}, transparent)` }}
                  />

                  <div className="flex items-center justify-between p-4 border-b border-[#2A2A2D]">
                    <div>
                      <span className="text-[9px] tracking-[0.2em] block mb-1" style={{ color: getColor(activeData.id) }}>
                        {GROUP_COLORS[activeData.group]?.label?.toUpperCase() || layer.name.toUpperCase()}
                      </span>
                      <h3 className="font-serif text-lg text-[#F5F5F5]" data-testid="info-panel-title">{activeData.name}</h3>
                    </div>
                    {!learningMode && (
                      <button onClick={() => setSelectedPart(null)} className="text-[#B8B8B8]/40 hover:text-[#F5F5F5] w-7 h-7 flex items-center justify-center" data-testid="close-panel">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex border-b border-[#2A2A2D]" data-testid="info-tabs">
                    {INFO_TABS.map(tab => {
                      const TIcon = tab.icon;
                      const accentColor = getColor(activeData.id);
                      return (
                        <button key={tab.id} onClick={() => setInfoTab(tab.id)}
                          className="flex-1 py-2.5 text-[9px] tracking-[0.1em] flex flex-col items-center gap-1 transition-all"
                          style={
                            infoTab === tab.id
                              ? { color: accentColor, borderBottom: `2px solid ${accentColor}`, background: hexToRgba(accentColor, 0.05) }
                              : { color: "rgba(184,184,184,0.4)", borderBottom: "2px solid transparent" }
                          }
                          data-testid={`tab-${tab.id}`}
                        >
                          <TIcon className="w-3.5 h-3.5" />
                          {tab.label.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>

                  <div className="p-4 min-h-[120px]">
                    <AnimatePresence mode="wait">
                      <motion.p key={infoTab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                        className="text-[#B8B8B8] text-sm leading-relaxed" data-testid="info-content"
                      >
                        {activeData[infoTab]}
                      </motion.p>
                    </AnimatePresence>
                  </div>

                  {/* Learning mode nav */}
                  {learningMode && (
                    <div className="flex items-center justify-between p-4 border-t border-[#2A2A2D]">
                      <button disabled={learningStep === 0} onClick={() => { setLearningStep(s => s - 1); setInfoTab("description"); }}
                        className="px-4 py-2 text-[10px] tracking-[0.1em] border border-[#2A2A2D] text-[#B8B8B8] hover:border-[#D4AF37]/40 disabled:opacity-20 disabled:cursor-default transition-all flex items-center gap-1"
                        data-testid="learning-prev"
                      >
                        <ArrowLeft className="w-3 h-3" /> WSTECZ
                      </button>
                      <span className="text-[10px] text-[#B8B8B8]/40">{learningStep + 1}/{layer.points.length}</span>
                      {learningStep < layer.points.length - 1 ? (
                        <button onClick={() => { setLearningStep(s => s + 1); setInfoTab("description"); }}
                          className="px-4 py-2 text-[10px] tracking-[0.1em] bg-[#D4AF37] text-[#0B0B0D] font-bold hover:bg-[#C8A96A] transition-all flex items-center gap-1"
                          data-testid="learning-next"
                        >
                          DALEJ <ArrowRight className="w-3 h-3" />
                        </button>
                      ) : (
                        <button onClick={() => setLearningMode(false)}
                          className="px-4 py-2 text-[10px] tracking-[0.1em] bg-[#D4AF37] text-[#0B0B0D] font-bold hover:bg-[#C8A96A] transition-all flex items-center gap-1"
                          data-testid="learning-finish"
                        >
                          ZAKOŃCZ <CheckCircle className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="bg-[#141416] border border-[#2A2A2D] p-8 text-center" data-testid="info-panel-empty"
                >
                  <div className="w-12 h-12 mx-auto mb-4 border border-[#D4AF37]/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#D4AF37]/25" />
                  </div>
                  <p className="text-[#B8B8B8]/40 text-sm mb-1">Kliknij na obszar na obrazku</p>
                  <p className="text-[#B8B8B8]/25 text-xs">lub uruchom tryb nauki</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── INTRO + KEY FUNCTIONS ── */}
      <section className="border-b border-[#2A2A2D] px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-[#B8B8B8] text-sm md:text-base leading-relaxed mb-10 max-w-3xl" data-testid="layer-intro">
              {layer.intro}
            </p>
            {layer.keyFunctions && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="key-functions">
                {layer.keyFunctions.map((fn, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 border border-[#2A2A2D] bg-[#141416]">
                    <Zap className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
                    <span className="text-[#B8B8B8]/70 text-xs leading-relaxed">{fn}</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── PROBLEMS / DISEASES ── */}
      {layer.problems && layer.problems.length > 0 && (
        <section className="border-b border-[#2A2A2D] px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-8">
                <AlertTriangle className="w-4 h-4 text-[#C87F4A]" />
                <span className="text-[10px] tracking-[0.3em] text-[#C87F4A]">PROBLEMY I CHOROBY</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="problems-section">
                {layer.problems.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    className="p-5 border border-[#2A2A2D] bg-[#141416] hover:border-[#C87F4A]/30 transition-colors"
                  >
                    <h4 className="text-[#F5F5F5] text-sm font-medium mb-2">{p.name}</h4>
                    <p className="text-[#B8B8B8]/50 text-xs leading-relaxed">{p.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── CIEKAWOSTKI (premium slider) ── */}
      {layer.funFacts && layer.funFacts.length > 0 && (
        <FunFactsSlider facts={layer.funFacts} />
      )}

      {/* ── QUIZ ── */}
      {quiz && quiz.length > 0 && (
        <section className="bg-[#141416] border-y border-[#2A2A2D] px-4 md:px-8 py-14">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">SPRAWDŹ WIEDZĘ</span>
              <h2 className="font-serif text-[clamp(1.2rem,2.5vw,1.8rem)] text-[#F5F5F5]" data-testid="quiz-h2">
                Mini quiz: {layer.name}
              </h2>
            </div>
            <Quiz questions={quiz} />
          </div>
        </section>
      )}

      {/* ── NEXT LAYER CTA ── */}
      <section className="px-4 md:px-8 py-14">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">KONTYNUUJ</span>
          <h2 className="font-serif text-[clamp(1.2rem,2.5vw,1.8rem)] text-[#F5F5F5] mb-8">Poznaj kolejne warstwy</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {LAYERS_META.filter(l => l.slug !== layer.slug).map(l => (
              <Link key={l.id} to={l.ready ? `/anatomia/owczarek-niemiecki/${l.slug}` : "#"}
                className={`px-6 py-2.5 text-xs tracking-[0.15em] transition-all flex items-center gap-2 ${
                  l.ready ? "border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0B0D]" : "border border-[#2A2A2D] text-[#B8B8B8]/25 cursor-default"
                }`}
              >
                {l.name.toUpperCase()} {l.ready && <ArrowRight className="w-3 h-3" />}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── COPYRIGHT ── */}
      <div className="border-t border-[#2A2A2D] px-4 py-6 text-center">
        <p className="text-[10px] tracking-[0.1em] text-[#B8B8B8]/30">
          Projekt graficzny i koncepcja &copy; <a href="https://apisdesign.online" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors">apisdesign.online</a>
        </p>
      </div>
    </div>
  );
};

export default AtlasLayer;
